import { Typography, Box, Button, Divider, Stack } from "@mui/material";
import { useEffect } from "react";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import useClientFormsByToken from "@/hooks/client/useClientFormsByToken";
import { useGetPackageSuggestionsBooking } from "@/hooks/client/useClientPackageSuggestions";
import useExperiments from "@/hooks/common/useExperiments";
import { OnlineStoreSource } from "@/hooks/onlineStore/useOnlineStoreMixpanelEvents";
import { Experiments } from "@/utils/experiments";
import { Experiment, Variant } from "../common/experiment/experiment";
import CheckMark from "../common/icons/checkMark";
import InfoCard from "../common/infoCard/infoCard";
import { PackageSuggestionBookingCard } from "./packageSuggestionBookingCard";

export default function BookingSuccessInfo() {
  const {
    visitId,
    clientAccessToken,
    medspaData: { id: medspaId, slug: medspaSlug },
    packagesSuggestionsV1Enabled,
    customizationEnabled, // This includes FF and Settings
    customizationPrimaryColor,
    affirmPromotionEnabled,
  } = useClientBookingContext();

  const { setClientAccessTokenCookie } = useClientAccessToken();

  const { visits, isAllFormsSubmitted } =
    useClientFormsByToken(clientAccessToken);

  const { suggestedPackages, loading, error } =
    useGetPackageSuggestionsBooking();

  useEffect(() => {
    if (clientAccessToken) {
      setClientAccessTokenCookie(clientAccessToken);
    }
  }, [clientAccessToken, setClientAccessTokenCookie]);

  const appointmentToken = visits?.find((visit) => visit.id == visitId)
    ?.appointment?.accessToken;

  const showPackageSuggestions =
    !loading &&
    !error &&
    packagesSuggestionsV1Enabled &&
    suggestedPackages.length > 0;

  const experimentVariants = useExperiments(
    Experiments.ExperimentClientPackageSuggestions
  );

  const getDescription = () => {
    if (affirmPromotionEnabled) {
      return "Explore packages and save on your appointment — payment options are available with Affirm!";
    }

    return "Explore packages and save on your appointment!";
  };

  return (
    <InfoCard>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
        <CheckMark />
      </Box>
      <Typography
        variant="h3"
        align="center"
        sx={{
          color: "text.secondary",
          pb: 3,
        }}
      >
        Your visit has been booked!
      </Typography>
      <Typography sx={{ pb: 3.5 }} align="center">
        A confirmation has been sent to your email address.
      </Typography>
      {!isAllFormsSubmitted && appointmentToken && (
        <Button
          fullWidth
          variant="contained"
          sx={{
            mb: 2,
          }}
          href={`/client/${medspaSlug}/appointments/${appointmentToken}`}
        >
          Complete required forms
        </Button>
      )}
      <Experiment
        code={Experiments.ExperimentClientPackageSuggestions}
        medspaId={medspaId}
      >
        <Variant code={experimentVariants.Control}>
          <></>
        </Variant>
        <Variant code={experimentVariants.VariantA}>
          {showPackageSuggestions && (
            <>
              <Divider sx={{ mt: 2, mb: 3 }} />
              <Stack spacing={2}>
                <Stack spacing={1}>
                  <Typography variant="h3">Save money with packages</Typography>
                  <Typography variant="paragraphSmall">
                    {getDescription()}
                  </Typography>
                </Stack>
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    overflowX: "auto",
                    scrollBehavior: "smooth",
                    overflowY: "hidden",
                    pb: 0.5,
                  }}
                >
                  {suggestedPackages.map((packageItem) => (
                    <PackageSuggestionBookingCard
                      key={packageItem.id}
                      medspaSlug={medspaSlug}
                      packageItem={packageItem}
                      customizationPrimaryColor={
                        customizationEnabled
                          ? customizationPrimaryColor
                          : undefined
                      }
                      onlineStoreTrackingSource={
                        OnlineStoreSource.CONFIRMATION_PAGE
                      }
                    />
                  ))}
                </Stack>
              </Stack>
            </>
          )}
        </Variant>
      </Experiment>
    </InfoCard>
  );
}
